import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Inbox, Users2, Clock, Printer, CheckCircle2, Volume2, VolumeX, AlarmClock, ChefHat } from "lucide-react";
import { useNotificationSound, useNewItemDetector } from "@/lib/sound";

const STATUS_META = {
  submitted: { label: "New request", color: "bg-amber-soft/40 text-ink", pulse: true },
  restaurant_accepted: { label: "Accepted", color: "bg-amber/20 text-ink" },
  rejected: { label: "Rejected", color: "bg-rose-100 text-rose-900" },
  confirmed: { label: "Confirmed", color: "bg-ink text-cream" },
  deposit_paid: { label: "Deposit paid", color: "bg-ink text-cream" },
  completed: { label: "Completed", color: "bg-green-100 text-green-900" },
  cancelled: { label: "Cancelled", color: "bg-tablo-border text-light-ink" },
  no_show: { label: "No show", color: "bg-rose-200 text-rose-900" },
  checked_in: { label: "Checked in", color: "bg-green-100 text-green-900" },
};

const POLL_MS = 15000;

function _hoursUntil(iso) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return (t - Date.now()) / 3600000;
}

function AcceptanceBadge({ booking }) {
  if (booking.dining_kind !== "package" || !booking.acceptance_deadline_at) return null;
  const hrs = _hoursUntil(booking.acceptance_deadline_at);
  if (hrs == null) return null;
  if (hrs <= 0) {
    return (
      <span className="tablo-pill bg-rose-100 text-rose-900 inline-flex items-center gap-1" data-testid="accept-overdue">
        <AlarmClock className="w-3 h-3" strokeWidth={1.6}/> Acceptance window closed
      </span>
    );
  }
  const label = hrs < 1 ? `${Math.round(hrs * 60)} min` : `${Math.round(hrs)} hr${Math.round(hrs) === 1 ? "" : "s"}`;
  return (
    <span className="tablo-pill bg-amber-soft/40 text-ink inline-flex items-center gap-1" data-testid="accept-window">
      <AlarmClock className="w-3 h-3" strokeWidth={1.6}/> Accept within {label}
    </span>
  );
}

export default function PartnerInbox() {
  const [list, setList] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [restaurant, setRestaurant] = useState(null);
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem("tablo_sound") === "on");
  const ding = useNotificationSound();

  const load = useCallback(async () => {
    try {
      const [{ data: inbox }, { data: me }] = await Promise.all([
        api.get("/partner/inbox"),
        api.get("/partner/me"),
      ]);
      setList(inbox);
      setRestaurant(me.restaurant);
      if (!activeId && inbox[0]) setActiveId(inbox[0].id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load inbox");
    }
  }, [activeId]);

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  // Detect newly-arrived submitted bookings → ding + toast
  useNewItemDetector(
    list.filter((b) => b.status === "submitted"),
    (additions) => {
      if (!additions.length) return;
      if (soundOn) ding();
      additions.forEach((b) => {
        toast(`New booking — ${b.organizer?.name || "Guest"} · ${b.pax} pax · ${b.date} ${b.time}`, {
          description: "Tap to review and accept.",
        });
      });
    }
  );

  const respond = async (id, action) => {
    try {
      await api.post(`/partner/bookings/${id}/respond`, { action });
      toast.success(`Booking ${action}ed`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const checkIn = async (id) => {
    try {
      await api.post(`/partner/bookings/${id}/check-in`);
      toast.success("Guest checked in — kitchen starts now.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const togglePartnerStatus = async () => {
    if (!restaurant) return;
    const newStatus = restaurant.partner_status === "active" ? "paused" : "active";
    try {
      await api.patch("/partner/settings", { partner_status: newStatus });
      toast.success(newStatus === "paused" ? "Now invisible to new bookers (rush mode)." : "Back online — bookers can find you.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const toggleSound = () => {
    const next = !soundOn;
    setSoundOn(next);
    localStorage.setItem("tablo_sound", next ? "on" : "off");
    if (next) ding();
  };

  const active = list.find((b) => b.id === activeId);
  const isPaused = restaurant?.partner_status === "paused";

  return (
    <div className="space-y-5 animate-fade-up">
      <header>
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <p className="text-amber text-xs uppercase tracking-[0.18em] font-medium mb-2">Inbox</p>
            <h1 className="font-serif text-3xl flex items-center gap-3">
              <Inbox className="w-7 h-7" strokeWidth={1.4}/> Incoming bookings
              {list.some((b) => b.status === "submitted") && (
                <span className="w-2.5 h-2.5 bg-amber rounded-full animate-pulse"/>
              )}
            </h1>
            <p className="text-light-ink text-sm">Tap Accept or Reject — bookers get notified instantly.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              onClick={togglePartnerStatus}
              className={isPaused ? "tablo-btn-primary" : "tablo-btn-outline"}
              data-testid="partner-active-toggle"
            >
              {isPaused ? "Resume taking bookings" : "Pause (rush mode)"}
            </Button>
            <Button onClick={toggleSound} className="tablo-btn-outline" data-testid="partner-sound-toggle">
              {soundOn ? <Volume2 className="w-4 h-4 mr-1.5" strokeWidth={1.6}/> : <VolumeX className="w-4 h-4 mr-1.5" strokeWidth={1.6}/>}
              Sound {soundOn ? "on" : "off"}
            </Button>
          </div>
        </div>
        {isPaused && (
          <div className="mt-3 bg-rose-100 text-rose-900 rounded-xl px-4 py-3 text-sm" data-testid="partner-paused-banner">
            You're <strong>paused</strong> — new guests won't see you in Discovery. Existing bookings are unaffected.
          </div>
        )}
      </header>

      <div className="grid md:grid-cols-[360px_1fr] gap-5">
        {/* List */}
        <div className="space-y-3" data-testid="partner-inbox-list">
          {list.length === 0 && <div className="tablo-card p-6 text-center text-light-ink text-sm">No requests yet.</div>}
          {list.map((b) => {
            const meta = STATUS_META[b.status] || { label: b.status, color: "" };
            return (
              <button
                key={b.id}
                onClick={() => setActiveId(b.id)}
                className={`w-full text-left tablo-card p-4 transition-shadow ${activeId === b.id ? "ring-2 ring-amber" : "hover:shadow-tablo-lg"}`}
                data-testid={`partner-inbox-card-${b.id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <p className="font-medium truncate">{b.organizer?.name || "Guest"}</p>
                  <span className={`tablo-pill ${meta.color} shrink-0`}>{meta.label}</span>
                </div>
                <p className="text-xs text-light-ink mt-1 flex gap-3 flex-wrap">
                  <span className="flex items-center gap-1"><Users2 className="w-3 h-3" strokeWidth={1.6}/>{b.pax}</span>
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" strokeWidth={1.6}/>{b.date} {b.time}</span>
                  <span className="flex items-center gap-1 text-light-ink">
                    {b.dining_kind === "package" ? <><ChefHat className="w-3 h-3" strokeWidth={1.6}/>Package</> : "À-la-carte"}
                  </span>
                </p>
                {b.status === "submitted" && (
                  <div className="mt-2"><AcceptanceBadge booking={b}/></div>
                )}
              </button>
            );
          })}
        </div>

        {/* Detail pane */}
        <div className="tablo-card p-6 min-h-[400px]" data-testid="partner-inbox-detail">
          {!active ? (
            <p className="text-light-ink">Select a request to see details.</p>
          ) : (
            <div className="space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-serif text-2xl">{active.organizer?.name || "Guest"}</p>
                  <p className="text-xs text-light-ink">{active.organizer?.org_name || "Personal booking"}</p>
                  {active.organizer?.tablo_contact && (
                    <p className="text-[11px] text-light-ink mt-1.5" data-testid="partner-tablo-contact">
                      ☎ Tablo Ops will reconfirm: <span className="font-medium text-ink">{active.organizer.tablo_contact}</span>
                    </p>
                  )}
                </div>
                <AcceptanceBadge booking={active}/>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><p className="text-[10px] uppercase text-light-ink">Guests</p><p className="font-serif text-xl">{active.pax}</p></div>
                <div><p className="text-[10px] uppercase text-light-ink">Date</p><p className="font-serif text-lg">{active.date}</p></div>
                <div><p className="text-[10px] uppercase text-light-ink">Time</p><p className="font-serif text-lg">{active.time}</p></div>
              </div>
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div><p className="text-light-ink">Mode</p><p className="font-medium capitalize">{active.dining_kind?.replace("_", "-")}</p></div>
                <div><p className="text-light-ink">Deposit</p><p className="font-medium">₹{active.deposit_amount_inr}</p></div>
                <div><p className="text-light-ink">Balance</p><p className="font-medium">₹{active.balance_amount_inr}</p></div>
              </div>

              {/* Guest dietary briefing — shown to the kitchen team */}
              {active.guest_preferences && (active.guest_preferences.allergens?.length > 0
                || active.guest_preferences.veg_preference === "veg_only"
                || (active.guest_preferences.spice_level != null && active.guest_preferences.spice_level !== 2)) && (
                <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-xs" data-testid="partner-guest-prefs">
                  <p className="text-rose-800 font-medium mb-1">⚠ Guest dietary briefing</p>
                  {active.guest_preferences.allergens?.length > 0 && (
                    <p className="text-rose-700">Allergens to avoid: <strong>{active.guest_preferences.allergens.join(", ")}</strong></p>
                  )}
                  {active.guest_preferences.veg_preference === "veg_only" && (
                    <p className="text-rose-700">Veg-only group — no non-veg dishes please.</p>
                  )}
                  {typeof active.guest_preferences.spice_level === "number" && (
                    <p className="text-rose-700">Spice tolerance: <strong>{["None", "Mild", "Medium", "Hot"][active.guest_preferences.spice_level]}</strong></p>
                  )}
                  {active.guest_preferences.drink_preferences?.length > 0 && (
                    <p className="text-rose-700">Drinks: {active.guest_preferences.drink_preferences.join(", ")}</p>
                  )}
                </div>
              )}
              {active.dining_kind === "package" && active.reconfirm_at && (
                <div className="bg-cream rounded-xl p-3 text-xs text-light-ink" data-testid="package-reconfirm-note">
                  <strong className="text-ink">Reconfirmation reminder:</strong> guest will be re-pinged on WhatsApp at{" "}
                  {new Date(active.reconfirm_at).toLocaleString("en-IN", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short" })} (T-3 hrs).
                </div>
              )}
              {active.dining_kind === "a_la_carte" && (
                <div className="bg-cream rounded-xl p-3 text-xs text-light-ink" data-testid="a-la-carte-note">
                  <strong className="text-ink">À-la-carte:</strong> prep starts only after the guest checks in at your venue. Confirm check-in below.
                </div>
              )}
              {active.notes && (
                <div className="bg-cream rounded-xl p-3 text-sm italic text-light-ink">"{active.notes}"</div>
              )}
              {active.status === "submitted" ? (
                <div className="flex gap-3 pt-2">
                  <Button onClick={() => respond(active.id, "accept")} className="flex-1 tablo-btn-primary" data-testid="partner-accept-btn">Accept</Button>
                  <Button onClick={() => respond(active.id, "reject")} className="flex-1 tablo-btn-outline" data-testid="partner-reject-btn">Reject</Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-light-ink">Current status: <span className="font-medium">{STATUS_META[active.status]?.label || active.status}</span></p>
                  <div className="flex gap-2 flex-wrap">
                    {(active.status === "confirmed" || active.status === "deposit_paid") && (
                      <Button onClick={() => checkIn(active.id)} className="tablo-btn-primary text-sm" data-testid="partner-checkin-btn">
                        <CheckCircle2 className="w-4 h-4 mr-1.5" strokeWidth={1.6}/> Confirm check-in
                      </Button>
                    )}
                    <Link to={`/print/${active.id}`} target="_blank" className="tablo-btn-outline text-sm inline-flex items-center" data-testid="partner-print-btn">
                      <Printer className="w-4 h-4 mr-1.5" strokeWidth={1.6}/> Print KOT
                    </Link>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
